/**
 * The portal's base domain: every tenant portal lives at <tenant>.<base>, and <base> itself is the
 * platform's landing page. Set at build time with VITE_PORTAL_BASE_DOMAIN (Pages build env var);
 * unset or invalid means bcportal.app. It must match the Worker's PORTAL_BASE_DOMAIN
 * (workers/src/shared/portalDomain.ts). docs/plans/DOMAIN-MIGRATION-GSPORTAL.md has the move plan.
 */
export const DEFAULT_PORTAL_BASE_DOMAIN = 'bcportal.app';

const HOSTNAME_RE = /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

/** A usable base domain from a raw value (lowercased, dots trimmed), or null. */
export function normalisePortalBaseDomain(raw: unknown): string | null {
  if (typeof raw !== 'string') { return null; }
  const v = raw.trim().toLowerCase().replace(/^\.+|\.+$/g, '');
  return HOSTNAME_RE.test(v) ? v : null;
}

const configured = import.meta.env.VITE_PORTAL_BASE_DOMAIN as string | undefined;
const parsed = normalisePortalBaseDomain(configured);
if (configured && !parsed) {
  console.warn(`[portalDomain] VITE_PORTAL_BASE_DOMAIN "${configured}" is not a domain; using ${DEFAULT_PORTAL_BASE_DOMAIN}`);
}

/** The base domain this build serves, e.g. "bcportal.app". */
export const PORTAL_BASE_DOMAIN: string = parsed ?? DEFAULT_PORTAL_BASE_DOMAIN;

/** ".bcportal.app": the suffix every tenant host ends with. */
export const PORTAL_HOST_SUFFIX = `.${PORTAL_BASE_DOMAIN}`;

/** The tenant part of a portal host ("acme" for acme.bcportal.app), or null for any other host. */
export function tenantOfPortalHost(hostname: string, base: string = PORTAL_BASE_DOMAIN): string | null {
  const h = hostname.toLowerCase();
  const suffix = `.${base}`;
  if (!h.endsWith(suffix)) { return null; }
  const sub = h.slice(0, -suffix.length);
  return sub || null;
}

/** True on the base domain itself or any host under it. */
export function isOnPortalDomain(hostname: string, base: string = PORTAL_BASE_DOMAIN): boolean {
  const h = hostname.toLowerCase();
  return h === base || h.endsWith(`.${base}`);
}

/** "acme.bcportal.app" for display. */
export function portalHost(subdomain: string, base: string = PORTAL_BASE_DOMAIN): string {
  return `${subdomain}.${base}`;
}
